/**
 * @fileoverview Shared callable API contract for the `setSectionTime`
 * Cloud Function, which writes a section time for the caller: a section
 * leader's section after the SL gate in
 * `firebase/documents/sectionSignupV2SlGate.ts`, or a student's onboarding
 * pick after a seats check.
 * Imported by both the functions handler and the webapp client so the
 * two sides cannot drift in name, request shape, or response shape.
 */
import {z} from 'zod';

import {SlGateRefusalSchema} from '../firebase/documents/sectionSignupV2SlGate';
import {FirestoreDocumentIdSchema} from '../firebase/types';

/**
 * Deployed Cloud Function name. Must match the exported function name
 * in the functions codebase so deployment and invocation resolve to
 * the same function.
 */
export const SET_SECTION_TIME_FUNCTION_NAME = 'setSectionTime';

/**
 * Server-side function timeout (`timeoutSeconds`). The handler runs one
 * Firestore transaction over the section-signup cache docs.
 */
export const SET_SECTION_TIME_SERVER_TIMEOUT_SECONDS = 20;

/**
 * How much longer the client waits than the server may run, so a call
 * that commits near the server deadline still returns its result
 * instead of a client timeout. Covers the network round trip and a cold
 * start's routing.
 */
const SET_SECTION_TIME_CLIENT_TIMEOUT_MARGIN_MS = 5_000;

/**
 * Client-side callable timeout: the server timeout plus
 * {@link SET_SECTION_TIME_CLIENT_TIMEOUT_MARGIN_MS}.
 */
export const SET_SECTION_TIME_TIMEOUT_MS =
  SET_SECTION_TIME_SERVER_TIMEOUT_SECONDS * 1000 +
  SET_SECTION_TIME_CLIENT_TIMEOUT_MARGIN_MS;

/**
 * Request body of the `setSectionTime` callable. The caller's course role
 * picks the path: students and experienced students set their own
 * onboarding pick, section leaders and staff set a section's time.
 */
export const SetSectionTimeRequestSchema = z.object({
  /** Course whose section time is being set. */
  courseId: FirestoreDocumentIdSchema,
  /**
   * Section to update. When omitted on the section path, the callable
   * creates the caller's section on this first pick. Ignored on the
   * student path.
   */
  sectionId: FirestoreDocumentIdSchema.optional(),
  /** Slot to move to, or `'none'` to leave the current slot. */
  timeIndex: z.union([z.number().int().min(0), z.literal('none')]),
});

/** See {@link SetSectionTimeRequestSchema}. */
export type SetSectionTimeRequest = z.infer<typeof SetSectionTimeRequestSchema>;

/** Slot counts behind a refusal, for the refusal copy and the logs. */
export const SetSectionTimeRefusalDetailSchema = z.object({
  /** The slot the failing test ran on; absent for `'none'`. */
  timeIndex: z.number().int().min(0).optional(),
  /** Countable SL sections in that slot before the move. */
  sections: z.number().int().nonnegative(),
  /** Students who have picked that slot. */
  students: z.number().int().nonnegative(),
  /** Seats left in that slot; negative when oversubscribed. */
  remainingSeats: z.number().int(),
  /** The slot's `slSlotCaps` entry, when it has one. */
  cap: z.number().int().nonnegative().optional(),
  /** Projected students at close, when the phase-2 test ran. */
  projectedStudents: z.number().nonnegative().optional(),
  /** For `'cross-course-conflict'`, the course whose section clashes. */
  otherCourseId: z.string().optional(),
  /** For `'cross-course-conflict'`, that section's slot in its course. */
  otherTimeIndex: z.number().int().min(0).optional(),
});

/** See {@link SetSectionTimeRefusalDetailSchema}. */
export type SetSectionTimeRefusalDetail = z.infer<
  typeof SetSectionTimeRefusalDetailSchema
>;

/**
 * Why a `'failed-precondition'` error was thrown, carried in the error's
 * `details` so the client can show specific copy:
 * - `'onboarding-not-open'`: the caller's onboarding window has not started.
 * - `'onboarding-closed'`: the caller's onboarding window has ended.
 * - `'signup-not-initialized'`: the course has no section signup meta doc.
 * - `'course-anchor-invalid'`: a section leader's pick needs the
 *   cross-course check, and a course it compares against has no usable
 *   schedule (`SectionCourseScheduleSchema`); `courseId` names that course.
 */
export const SetSectionTimeErrorReasonSchema = z.enum([
  'onboarding-not-open',
  'onboarding-closed',
  'signup-not-initialized',
  'course-anchor-invalid',
]);

/** See {@link SetSectionTimeErrorReasonSchema}. */
export type SetSectionTimeErrorReason = z.infer<
  typeof SetSectionTimeErrorReasonSchema
>;

/** The `details` payload of a `setSectionTime` error that has a reason. */
export const SetSectionTimeErrorDetailsSchema = z.object({
  reason: SetSectionTimeErrorReasonSchema,
  /** For `'course-anchor-invalid'`, the course with the unusable schedule. */
  courseId: z.string().optional(),
});

/** See {@link SetSectionTimeErrorDetailsSchema}. */
export type SetSectionTimeErrorDetails = z.infer<
  typeof SetSectionTimeErrorDetailsSchema
>;

/**
 * Why `setSectionTime` refused: an SL gate test,
 * `'cross-course-conflict'` when the section leader's section in another
 * course meets too close to the requested slot (see
 * `checkCrossCourseSectionTime`), `'slot-closed'` when staff closed the
 * slot to section leaders with "Edit Section Times" (see
 * `getClosedSlSignupSlots`), or `'student-slot-full'` when a student picks
 * a slot with no seats left.
 */
export const SetSectionTimeRefusalReasonSchema = z.enum([
  ...SlGateRefusalSchema.options,
  'cross-course-conflict',
  'slot-closed',
  'student-slot-full',
]);

/** See {@link SetSectionTimeRefusalReasonSchema}. */
export type SetSectionTimeRefusalReason = z.infer<
  typeof SetSectionTimeRefusalReasonSchema
>;

/** Response body of the `setSectionTime` callable. */
export const SetSectionTimeResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    /**
     * The section written, including one created on a first pick. Absent
     * for a student's pick, which writes no section.
     */
    sectionId: z.string().optional(),
  }),
  z.object({
    status: z.literal('refused'),
    /** Which test refused the move. */
    reason: SetSectionTimeRefusalReasonSchema,
    /** Counts for the slot the refusing test ran on. */
    detail: SetSectionTimeRefusalDetailSchema,
  }),
]);

/** See {@link SetSectionTimeResponseSchema}. */
export type SetSectionTimeResponse = z.infer<
  typeof SetSectionTimeResponseSchema
>;
